/**
 * Knowledge Graph → KYC Form Field Mapping
 * TASK-371: AI Copilot Documents → Knowledge Graph → Form Filling
 *
 * Maps backend KG field paths (snake_case, extraction schema canonical)
 * to frontend KYC form paths (camelCase, KYCFormData interface).
 */

import type { KnowledgeGraphEntry } from '../types/complianceWorkflow';

// ============================================================================
// Static Mapping
// ============================================================================

export const KG_TO_FORM_FIELD_MAP: Record<string, string> = {
  // Personal
  'personal.first_name': 'personalInfo.firstName',
  'personal.middle_name': 'personalInfo.middleName',
  'personal.last_name': 'personalInfo.lastName',
  'personal.date_of_birth': 'personalInfo.dateOfBirth',
  'personal.nationality': 'personalInfo.nationalities',
  'personal.dual_nationality': 'personalInfo.nationalities',
  'personal.gender': 'personalInfo.gender',
  'personal.marital_status': 'personalInfo.maritalStatus',
  'personal.passport_number': 'personalInfo.passportNumber',
  'personal.place_of_birth': 'personalInfo.placeOfBirth',
  'personal.national_id': 'personalInfo.nationalId',
  'personal.ssn': 'personalInfo.ssn',

  // Contact — Phone & Email
  'contact.phone': 'contactInfo.phonePrimary',
  'contact.phone_secondary': 'contactInfo.phoneSecondary',
  'contact.email': 'contactInfo.emailPrimary',
  'contact.email_secondary': 'contactInfo.emailSecondary',
  'contact.preferred_contact_method': 'contactInfo.preferredContactMethod',

  // Contact — Residential Address
  'contact.address_line_1': 'contactInfo.residentialAddress.street',
  'contact.address_line_2': 'contactInfo.residentialAddress.apartment',
  'contact.city': 'contactInfo.residentialAddress.city',
  'contact.state': 'contactInfo.residentialAddress.state',
  'contact.postal_code': 'contactInfo.residentialAddress.postalCode',
  'contact.country': 'contactInfo.residentialAddress.country',

  // Contact — Relationship Information
  'contact.relationship_reason': 'contactInfo.relationshipReason',
  'contact.banking_relationship_reasons': 'contactInfo.relationshipReason',
  'contact.relationship_purpose': 'contactInfo.relationshipPurpose',
  'contact.investment_goals': 'contactInfo.relationshipPurpose',

  // Employment
  'employment.status': 'employmentInfo.employmentStatus',
  'employment.employer_name': 'employmentInfo.employerName',
  'employment.job_title': 'employmentInfo.occupation',
  'employment.industry': 'employmentInfo.employmentIndustry',
  'employment.employer_country': 'employmentInfo.workAddress.country',
  'employment.years_employed': 'employmentInfo.yearsEmployed',
  'employment.annual_income': 'employmentInfo.annualIncome',
  // TASK-616: employment.history[N] carries a whole past position as one JSONB
  // object; assembleEmploymentHistory() folds the indexed keys into an array.
  'employment.history': 'employmentInfo.history',
  'employment.start_date': 'employmentInfo.startDate',

  // Tax
  'tax.tax_residence': 'taxInfo.taxResidence',
  'tax.tax_id': 'taxInfo.taxId',
  'tax.us_person': 'taxInfo.usPerson',
  'tax.us_ssn': 'taxInfo.usSSN',
  'tax.fatca_status': 'taxInfo.fatcaStatus',
  'tax.tax_exempt': 'taxInfo.taxExempt',
  'tax.tax_exempt_reason': 'taxInfo.taxExemptReason',

  // Wealth — summary fields are TOP-LEVEL on KYCFormData (not nested under wealthSources)
  'wealth.total_net_worth': 'totalNetWorth',
  'wealth.liquid_assets': 'liquidNetWorth',
  'wealth.net_worth_currency': 'netWorthCurrency',
  'wealth.source_of_wealth': 'primaryWealthSource',
  'wealth.source_of_funds': 'sourceDocumentation',
  'wealth.wealth_narrative': 'wealthNarrative',

  // Wealth — asset breakdown percentages (TOP-LEVEL)
  'wealth.asset_breakdown_cash': 'assetBreakdownCash',
  'wealth.asset_breakdown_securities': 'assetBreakdownSecurities',
  'wealth.asset_breakdown_real_estate': 'assetBreakdownRealEstate',
  'wealth.asset_breakdown_business_equity': 'assetBreakdownBusinessEquity',
  'wealth.asset_breakdown_commodities': 'assetBreakdownCommodities',
  'wealth.asset_breakdown_crypto': 'assetBreakdownCrypto',
  'wealth.asset_breakdown_other': 'assetBreakdownOther',

  // Wealth — additional information (TOP-LEVEL)
  'wealth.inheritance_expected': 'inheritanceExpected',
  'wealth.inheritance_details': 'inheritanceDetails',
  'wealth.unusual_transactions': 'unusualTransactions',
  'wealth.unusual_transaction_details': 'unusualTransactionDetails',

  // Wealth — detailed source breakdown (staging fields → assembled into wealthSources array)
  // source_type starts a new entry; other fields attach to the current entry.
  'wealth.source_type': 'wealthSources._ws_type',
  'wealth.source_percentage': 'wealthSources._ws_percentage',
  'wealth.source_amount': 'wealthSources._ws_amount',
  'wealth.source_description': 'wealthSources._ws_description',
  'wealth.source_start_date': 'wealthSources._ws_start_date',
  'wealth.source_end_date': 'wealthSources._ws_end_date',
  'wealth.source_ongoing': 'wealthSources._ws_ongoing',
  'wealth.source_asset_type': 'wealthSources._ws_asset_type',
  'wealth.source_asset_location': 'wealthSources._ws_asset_location',
  'wealth.source_liquidity_level': 'wealthSources._ws_liquidity_level',

  // Income — summary fields are TOP-LEVEL on KYCFormData (not nested under incomeSources)
  'income.annual_income': 'totalAnnualIncome',
  'income.income_currency': 'incomeCurrency',
  'income.primary_income_type': 'primaryIncomeType',

  // Income — detailed source breakdown (staging fields → assembled into incomeSources.sources array)
  // source_type starts a new entry; other fields attach to the current entry.
  'income.source_type': 'incomeSources._is_type',
  'income.source_name': 'incomeSources._is_name',
  'income.source_description': 'incomeSources._is_description',
  'income.source_amount': 'incomeSources._is_amount',
  'income.source_currency': 'incomeSources._is_currency',
  'income.source_frequency': 'incomeSources._is_frequency',
  'income.source_country': 'incomeSources._is_country',
  'income.source_start_date': 'incomeSources._is_start_date',
  'income.source_ongoing': 'incomeSources._is_ongoing',
  'income.source_stability': 'incomeSources._is_stability',
  'income.source_continuity': 'incomeSources._is_continuity',

  // Income — future income expectations (stored on incomeSources object, not in sources array)
  'income.expected_income_change': 'incomeSources.expectedIncomeChange',
  'income.expected_change_details': 'incomeSources.expectedChangeDetails',
  'income.retirement_income': 'incomeSources.retirementIncome',
  'income.retirement_income_details': 'incomeSources.retirementIncomeDetails',
  'income.other_financial_support': 'incomeSources.otherFinancialSupport',
  'income.other_support_details': 'incomeSources.otherSupportDetails',

  // PEP / Adverse Media
  'pep.is_pep': 'adverseMedia.isPEP',
  'pep.pep_type': 'adverseMedia.pepType',
  'pep.pep_function': 'adverseMedia.pepPosition',
  'pep.pep_country': 'adverseMedia.pepCountry',
  'pep.pep_start_date': 'adverseMedia.pepStartDate',
  'pep.pep_end_date': 'adverseMedia.pepEndDate',
  'pep.pep_relationship': 'adverseMedia.rcaRelationship',
  'pep.is_rca': 'adverseMedia.isRCA',
  'pep.rca_pep_name': 'adverseMedia.rcaPEPName',

  // Adverse Media & Sanctions
  'adverse_media.has_adverse_media': 'adverseMedia.hasAdverseMedia',
  'adverse_media.adverse_media_details': 'adverseMedia.adverseMediaDetails',
  'adverse_media.sanctions_screened': 'adverseMedia.sanctionsScreened',
  'adverse_media.sanctions_hit': 'adverseMedia.sanctionsHit',
  'adverse_media.sanctions_details': 'adverseMedia.sanctionsDetails',
  'adverse_media.reputational_risk': 'adverseMedia.reputationalRisk',
  'adverse_media.insider_flag': 'adverseMedia.insiderFlag',

  // Board Positions (Adverse Media step — array of objects)
  'adverse_media.board_company': 'adverseMedia._board_company',
  'adverse_media.board_position': 'adverseMedia._board_position',
  'adverse_media.board_country': 'adverseMedia._board_country',
  'adverse_media.board_current': 'adverseMedia._board_current',
  'adverse_media.board_publicly_traded': 'adverseMedia._board_publicly_traded',
  'adverse_media.board_isin': 'adverseMedia._board_isin',

  // Business Activities (maps to first business in the array)
  // All staging fields MUST use _business_ prefix so the staging assembly logic matches them.
  'business.company_name': 'businessActivities._business_name',
  'business.business_type': 'businessActivities._business_type',
  'business.registration_number': 'businessActivities._business_number',
  'business.registration_country': 'businessActivities._business_country',
  'business.industry': 'businessActivities._business_industry',
  'business.ownership_percentage': 'businessActivities._business_percentage',
  'business.annual_revenue': 'businessActivities._business_revenue',
  'business.cross_border_transactions': 'businessActivities._business_cross_border',
  'business.cash_intensive': 'businessActivities._business_cash_intensive',

  // Personal Background (Step 4)
  'background.education_level': 'personalBackground.educationLevel',
  'background.institution_name': 'personalBackground._institution_name',
  'background.institution_degree': 'personalBackground._institution_degree',
  'background.institution_year': 'personalBackground._institution_year',
  'background.certification_name': 'personalBackground._certification_name',
  'background.certification_issuer': 'personalBackground._certification_issuer',
  'background.certification_year': 'personalBackground._certification_year',
  'background.hobbies_interests': 'personalBackground.hobbiesInterests',
  'background.family_composition': 'personalBackground.familyComposition',
  'background.dependents_count': 'personalBackground.dependentsCount',
  'background.spouse_employment_status': 'personalBackground.spousePartnerEmploymentStatus',
  // Legacy paths (LLM may use personal.* prefix)
  // NOTE: 'personal.education' intentionally omitted — LLM stores narrative
  // text there (biography), NOT the education_level enum.
  'personal.education_level': 'personalBackground.educationLevel',
  'personal.interests': 'personalBackground.hobbiesInterests',
  'personal.hobbies_interests': 'personalBackground.hobbiesInterests',
  'personal.family_members': 'personalBackground.familyComposition',
  'personal.family_composition': 'personalBackground.familyComposition',
  'personal.dependents_count': 'personalBackground.dependentsCount',
  'personal.spouse_employment_status': 'personalBackground.spousePartnerEmploymentStatus',
};

// Fields where the KG stores a scalar but the form expects an array.
// dual_nationality maps to the same form path as nationality.
const ARRAY_FIELDS = new Set(['personalInfo.nationalities', 'personalBackground.hobbiesInterests']);

// ============================================================================
// Value Normalization — maps backend/LLM values to frontend form enum values
// ============================================================================

/** Explicit value maps for fields where backend values differ from form enums */
const VALUE_MAPS: Record<string, Record<string, string>> = {
  employmentStatus: {
    'employed': 'employed',
    'self-employed': 'selfEmployed',
    'self_employed': 'selfEmployed',
    'selfemployed': 'selfEmployed',
    'unemployed': 'unemployed',
    'retired': 'retired',
    'student': 'student',
    'other': 'other',
  },
  preferredContactMethod: {
    'email': 'email',
    'phone': 'phone',
    'whatsapp': 'whatsapp',
    'video_call': 'video_call',
    'video call': 'video_call',
    'videocall': 'video_call',
    'in_person': 'in_person',
    'in person': 'in_person',
    'inperson': 'in_person',
  },
  educationLevel: {
    'high school': 'high_school',
    'high_school': 'high_school',
    'highschool': 'high_school',
    'bachelor': 'bachelor',
    "bachelor's": 'bachelor',
    'bachelors': 'bachelor',
    "bachelor's degree": 'bachelor',
    'master': 'master',
    "master's": 'master',
    'masters': 'master',
    "master's degree": 'master',
    'mba': 'professional_degree',
    'phd': 'phd',
    'doctorate': 'phd',
    'professional_degree': 'professional_degree',
    'professional degree': 'professional_degree',
    'other': 'other',
  },
  spousePartnerEmploymentStatus: {
    'employed': 'employed',
    'self-employed': 'selfEmployed',
    'self_employed': 'selfEmployed',
    'selfemployed': 'selfEmployed',
    'unemployed': 'unemployed',
    'retired': 'retired',
    'homemaker': 'homemaker',
    'other': 'other',
  },
  incomeType: {
    'salary': 'salary',
    'employment': 'salary',
    'wages': 'salary',
    'bonus': 'bonus',
    'dividends': 'dividends',
    'dividend': 'dividends',
    'participation': 'dividends',
    'rental': 'rental',
    'rent': 'rental',
    'rental_income': 'rental',
    'pension': 'pension',
    'retirement': 'pension',
    'social_security': 'social_security',
    'investment': 'investment',
    'securities': 'investment',
    'capital_gains': 'investment',
    'interest': 'investment',
    'business': 'business',
    'consulting': 'business',
    'freelance': 'business',
    'alimony': 'alimony',
    'trust': 'trust',
    'royalties': 'royalties',
    'other': 'other',
  },
  primaryWealthSource: {
    'employment': 'employment',
    'salary': 'employment',
    'business': 'business',
    'business_ownership': 'business',
    'business ownership': 'business',
    'inheritance': 'inheritance',
    'inherited': 'inheritance',
    'investment': 'investment',
    'investment_income': 'investment',
    'investment income': 'investment',
    'capital_gains': 'investment',
    'real_estate': 'real_estate',
    'real estate': 'real_estate',
    'property': 'real_estate',
    'rental': 'real_estate',
    'gift': 'gift',
    'donation': 'gift',
    'lottery': 'lottery',
    'gambling': 'lottery',
    'savings': 'savings',
    'pension': 'pension',
    'retirement': 'pension',
    'other': 'other',
  },
};

/** Boolean form fields */
const BOOLEAN_FIELDS = new Set([
  'isPEP', 'isRCA', 'usPerson', 'taxExempt', 'insiderFlag',
  'hasAdverseMedia', 'sanctionsScreened', 'sanctionsHit',
  'hasBusinessActivities',
  'inheritanceExpected', 'unusualTransactions',
  'expectedIncomeChange', 'retirementIncome', 'otherFinancialSupport',
]);

/** Enum fields normalized to lowercase_underscore */
const ENUM_FIELDS = new Set([
  'gender', 'maritalStatus',
  'employmentIndustry',
  'fatcaStatus',
  'pepType', 'rcaRelationship', 'reputationalRisk',
  'primaryIncomeType',
  'businessType', 'industryRiskLevel',
]);

/** Currency fields → always UPPERCASE (form selects use CHF/EUR/USD/GBP) */
const CURRENCY_FIELDS = new Set(['netWorthCurrency', 'incomeCurrency']);

/** Numeric form fields — KG stores as strings, form expects numbers */
const NUMERIC_FIELDS = new Set([
  'dependentsCount', 'yearsEmployed', 'annualIncome',
  'totalNetWorth', 'liquidNetWorth', 'totalAnnualIncome',
  'assetBreakdownCash', 'assetBreakdownSecurities', 'assetBreakdownRealEstate',
  'assetBreakdownBusinessEquity', 'assetBreakdownCommodities', 'assetBreakdownCrypto',
  'assetBreakdownOther',
]);

/**
 * Normalize a KG value to match form enum/boolean expectations.
 * Uses the last segment of the form path to determine normalization rules.
 */
function normalizeFormValue(formPath: string, value: any): any {
  if (typeof value !== 'string' || !value) return value;

  const lastPart = formPath.split('.').pop() || '';

  // Explicit value maps
  if (VALUE_MAPS[lastPart]) {
    const lower = value.toLowerCase();
    const normalized = lower.replace(/[\s-]+/g, '_').replace(/_+/g, '_');
    const exact = VALUE_MAPS[lastPart][lower] ?? VALUE_MAPS[lastPart][normalized];
    if (exact) return exact;

    // Keyword fallback for long narrative values (e.g. primaryWealthSource)
    // Check if any VALUE_MAP key appears as a substring in the value
    const map = VALUE_MAPS[lastPart];
    for (const [mapKey, mapVal] of Object.entries(map)) {
      if (lower.includes(mapKey)) return mapVal;
    }
    return lower;
  }

  // Boolean fields
  if (BOOLEAN_FIELDS.has(lastPart)) {
    return value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
  }

  // Currency fields → uppercase (CHF, EUR, USD, GBP)
  if (CURRENCY_FIELDS.has(lastPart)) {
    return value.toUpperCase().trim();
  }

  // Enum fields → lowercase with underscored spaces
  if (ENUM_FIELDS.has(lastPart)) {
    return value.toLowerCase().replace(/\s+/g, '_');
  }

  // Numeric fields → parse string to number
  if (NUMERIC_FIELDS.has(lastPart)) {
    const num = parseFloat(value.replace(/[,'\s]/g, ''));
    return isNaN(num) ? value : num;
  }

  return value;
}

// ============================================================================
// Staging Field Assembly — converts _prefix_ flat fields into arrays
// ============================================================================

/**
 * Staging field configs mirror the ones in KYCWizardContext.applyExtractionProposal.
 * Each config describes how flat staging keys (e.g. _institution_name) within a
 * section object get assembled into a typed array entry.
 */
const STAGING_CONFIGS: Array<{
  prefix: string;
  section: string;
  arrayKey: string;
  keyMap: Record<string, string>;
  numericKeys: string[];
  booleanKeys: string[];
}> = [
  {
    prefix: '_institution_',
    section: 'personalBackground',
    arrayKey: 'institutionsAttended',
    keyMap: { name: 'name', degree: 'degree', year: 'graduationYear' },
    numericKeys: ['graduationYear'],
    booleanKeys: [],
  },
  {
    prefix: '_certification_',
    section: 'personalBackground',
    arrayKey: 'professionalCertifications',
    keyMap: { name: 'name', issuer: 'issuer', year: 'year' },
    numericKeys: ['year'],
    booleanKeys: [],
  },
  {
    prefix: '_business_',
    section: 'businessActivities',
    arrayKey: 'businesses',
    keyMap: { name: 'businessName', type: 'businessType', number: 'registrationNumber', country: 'registrationCountry', industry: 'industry', percentage: 'ownershipPercentage', revenue: 'annualRevenue', cross_border: 'crossBorderTransactions', cash_intensive: 'cashIntensive' },
    numericKeys: ['ownershipPercentage', 'annualRevenue'],
    booleanKeys: ['crossBorderTransactions', 'cashIntensive'],
  },
  {
    prefix: '_board_',
    section: 'adverseMedia',
    arrayKey: 'boardPositions',
    keyMap: { company: 'company', position: 'position', country: 'country', current: 'current', publicly_traded: 'isPubliclyTraded', isin: 'isin' },
    numericKeys: [],
    booleanKeys: ['current', 'isPubliclyTraded'],
  },
  {
    prefix: '_ws_',
    section: 'wealthSources',
    arrayKey: 'sources',  // WealthSourceStep reads: ws.sources (line 105)
    keyMap: { type: 'sourceType', percentage: 'percentage', amount: 'amount', description: 'description', start_date: 'acquisitionStartDate', end_date: 'acquisitionEndDate', ongoing: 'isOngoing', asset_type: 'assetType', asset_location: 'assetLocation', liquidity_level: 'liquidityLevel' },
    numericKeys: ['percentage', 'amount'],
    booleanKeys: ['isOngoing'],
  },
  {
    prefix: '_is_',
    section: 'incomeSources',
    arrayKey: 'sources',  // IncomeSourceStep reads: is.sources
    keyMap: { type: 'incomeType', name: 'sourceName', description: 'incomeDescription', amount: 'annualAmount', currency: 'currency', frequency: 'frequency', country: 'sourceCountry', start_date: 'startDate', ongoing: 'isOngoing', stability: 'stabilityRating', continuity: 'expectedContinuity' },
    numericKeys: ['annualAmount'],
    booleanKeys: ['isOngoing'],
  },
];

/**
 * Post-process the built form data to assemble staging fields into arrays.
 *
 * After `setNestedValue`, staging fields exist as flat keys on section objects:
 *   personalBackground._institution_name = "Harvard"
 *   personalBackground._institution_degree = "Economics"
 *
 * This function groups them into the expected array shape:
 *   personalBackground.institutionsAttended = [{ name: "Harvard", degree: "Economics" }]
 *
 * Supports indexed keys (e.g. `_ws_type[0]`, `_ws_type[1]`) for multiple
 * array entries persisted with index suffixes.
 */
function assembleStagingFields(data: Record<string, any>): void {
  for (const config of STAGING_CONFIGS) {
    const section = data[config.section];
    if (!section || typeof section !== 'object') continue;

    // Collect staging keys, stripping optional [N] index suffix
    const indexed: Map<number, Record<string, any>> = new Map();
    // Track the "starter" subField for this config
    const nameKey = Object.values(config.keyMap).includes('sourceType')
      ? 'type'
      : Object.values(config.keyMap).includes('incomeType')
        ? 'type'
        : Object.keys(config.keyMap)[0];
    let nextIdx = 0;

    // Sort keys to ensure deterministic ordering (indexed keys sort naturally)
    const sortedKeys = Object.keys(section).filter(k => k.startsWith(config.prefix)).sort();

    for (const key of sortedKeys) {
      // Strip optional [N] index: _ws_type[0] → _ws_type, index=0
      const idxMatch = key.match(/^(.+)\[(\d+)\]$/);
      let baseKey = key;
      let explicitIdx: number | null = null;
      if (idxMatch) {
        baseKey = idxMatch[1];
        explicitIdx = parseInt(idxMatch[2], 10);
      }

      const subField = baseKey.slice(config.prefix.length);
      const objKey = config.keyMap[subField] || subField;

      let finalVal: any = section[key];
      if (config.numericKeys.includes(objKey) && typeof finalVal === 'string') {
        finalVal = parseFloat(finalVal) || finalVal;
      } else if (config.booleanKeys.includes(objKey)) {
        finalVal = finalVal === true || finalVal === 'true' || finalVal === '1';
      }

      // Determine which array entry this belongs to
      let idx: number;
      if (explicitIdx !== null) {
        idx = explicitIdx;
      } else if (subField === nameKey) {
        // Non-indexed starter field → new entry
        idx = nextIdx++;
      } else {
        // Non-indexed attribute → attach to latest entry
        idx = Math.max(0, nextIdx - 1);
      }

      if (!indexed.has(idx)) indexed.set(idx, {});
      indexed.get(idx)![objKey] = finalVal;

      delete section[key];
    }

    if (indexed.size > 0) {
      // Build array sorted by index
      const entries = [...indexed.entries()]
        .sort(([a], [b]) => a - b)
        .map(([, entry]) => entry);
      section[config.arrayKey] = entries;
      if (config.section === 'businessActivities') {
        section.hasBusinessActivities = true;
      }
    }
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Strip surrounding double-quotes from a string value.
 * Fixes double-JSON-encoded values stored by earlier versions of
 * KnowledgeGraphService.upsert_field() which called json.dumps()
 * before asyncpg's JSONB codec serialised the value again.
 *
 * e.g. '"male"' → 'male', '"Jorge"' → 'Jorge', 'normal' → 'normal'
 */
export function cleanKGValue(value: any): any {
  if (typeof value === 'string') {
    let s = value;
    // Iteratively strip encoding artifacts (handles any depth of encoding)
    let changed = true;
    while (changed && s.length >= 2) {
      changed = false;
      // Pattern: \"value\" (literal backslash-quote from triple-encoding)
      if (s.startsWith('\\"') && s.endsWith('\\"')) {
        s = s.slice(2, -2);
        changed = true;
      // Pattern: "value" (double-encoded JSON quotes)
      } else if (s.startsWith('"') && s.endsWith('"')) {
        s = s.slice(1, -1);
        changed = true;
      }
    }
    return s;
  }
  if (Array.isArray(value)) {
    return value.map(cleanKGValue);
  }
  return value;
}

/**
 * Map a single KG field_path to its frontend form path.
 * Handles indexed paths like `wealth.source_type[0]` by stripping the
 * index for lookup and re-appending it to the mapped result.
 * Returns the original path unchanged if no mapping exists.
 */
export function mapKGFieldToFormPath(kgFieldPath: string): string {
  // Check for indexed suffix: field_path[N]
  const idxMatch = kgFieldPath.match(/^(.+?)(\[\d+\])$/);
  if (idxMatch) {
    const basePath = idxMatch[1];
    const suffix = idxMatch[2];
    const mapped = KG_TO_FORM_FIELD_MAP[basePath];
    return mapped ? `${mapped}${suffix}` : kgFieldPath;
  }
  return KG_TO_FORM_FIELD_MAP[kgFieldPath] ?? kgFieldPath;
}

/**
 * Map a single KG entry { field_path, value } to form-compatible { formPath, value }.
 * Handles scalar-to-array coercion for fields like nationalities.
 * Also cleans double-encoded string values (strips spurious surrounding quotes).
 */
export function mapKGEntryToFormEntry(
  kgFieldPath: string,
  value: any
): { formPath: string; value: any } {
  const formPath = mapKGFieldToFormPath(kgFieldPath);
  const cleaned = cleanKGValue(value);

  // Normalize enum/boolean values to match form expectations
  const normalized = normalizeFormValue(formPath, cleaned);

  // Coerce scalar → array for array-typed form fields
  if (ARRAY_FIELDS.has(formPath) && !Array.isArray(normalized)) {
    // Split comma-separated strings into individual items
    if (typeof normalized === 'string' && normalized.includes(',')) {
      return { formPath, value: normalized.split(',').map((s: string) => s.trim()).filter(Boolean) };
    }
    return { formPath, value: [normalized] };
  }

  return { formPath, value: normalized };
}

/**
 * Build a nested partial form object from mapped entries.
 * Sets deeply-nested keys from dot-separated formPaths.
 */
export function buildFormDataFromKGEntries(
  entries: Array<{ field_path: string; value: any; confidence: number; needs_reconfirm?: boolean }>
): {
  data: Record<string, any>;
  confidence: Record<string, number>;
  needsReconfirm: Record<string, boolean>;
} {
  const data: Record<string, any> = {};
  const confidence: Record<string, number> = {};
  // TASK-617: form paths flagged for RM re-confirmation after a transition.
  const needsReconfirm: Record<string, boolean> = {};

  for (const entry of entries) {
    const { formPath, value } = mapKGEntryToFormEntry(entry.field_path, entry.value);
    setNestedValue(data, formPath, value);
    confidence[formPath] = entry.confidence;
    if (entry.needs_reconfirm) needsReconfirm[formPath] = true;
  }

  // Assemble staging fields (_institution_*, _certification_*, _business_*, _board_*)
  // into their typed array counterparts (institutionsAttended, etc.)
  assembleStagingFields(data);

  // TASK-616: fold employmentInfo["history[N]"] keys into a sorted array
  assembleEmploymentHistory(data);

  return { data, confidence, needsReconfirm };
}

/** snake_case → camelCase for the leaf keys inside a history payload. */
function toCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/**
 * TASK-616: KG stores each past position as one JSONB row at
 * `employment.history[N]`, which setNestedValue lands as the literal key
 * `history[0]`, `history[1]`, … on employmentInfo. Fold those into
 * `employmentInfo.history`, newest interval first, so the form renders a
 * timeline instead of numbered keys.
 */
function assembleEmploymentHistory(data: Record<string, any>): void {
  const emp = data.employmentInfo;
  if (!emp || typeof emp !== 'object') return;

  const entries: Array<{ index: number; position: Record<string, any> }> = [];
  for (const key of Object.keys(emp)) {
    const match = key.match(/^history\[(\d+)\]$/);
    if (!match) continue;
    const raw = emp[key];
    delete emp[key];
    if (!raw || typeof raw !== 'object') continue;
    const position: Record<string, any> = {};
    for (const [k, v] of Object.entries(raw)) position[toCamel(k)] = v;
    entries.push({ index: Number(match[1]), position });
  }
  if (!entries.length) return;

  // Newest interval first; fall back to the slot index when validTo is absent
  // (pre-TASK-616 rows never carried an interval).
  entries.sort((a, b) => {
    const at = a.position.validTo;
    const bt = b.position.validTo;
    if (at && bt) return String(bt).localeCompare(String(at));
    if (at) return -1;
    if (bt) return 1;
    return b.index - a.index;
  });

  const existing = Array.isArray(emp.history) ? emp.history : [];
  emp.history = [...entries.map((e) => e.position), ...existing];
}

/**
 * Set a value at a dot-separated path in a nested object.
 * For array fields, merges with existing array values instead of replacing.
 */
function setNestedValue(obj: Record<string, any>, path: string, value: any): void {
  const segments = path.split('.');
  let current = obj;

  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    if (!current[seg] || typeof current[seg] !== 'object') {
      current[seg] = {};
    }
    current = current[seg];
  }

  const lastKey = segments[segments.length - 1];

  // Merge arrays instead of replacing
  if (ARRAY_FIELDS.has(path) && Array.isArray(current[lastKey]) && Array.isArray(value)) {
    const existing = new Set(current[lastKey]);
    for (const v of value) existing.add(v);
    current[lastKey] = [...existing];
  } else {
    current[lastKey] = value;
  }
}

// ============================================================================
// Domain Labels & Partition Helpers (Extraction Review Flow)
// ============================================================================

/** Domain display labels ordered to match KYC wizard steps. */
export const DOMAIN_LABELS: Record<string, string> = {
  personal: 'Personal Information',
  contact: 'Contact Details',
  employment: 'Employment Information',
  background: 'Personal Background',
  tax: 'Tax Information',
  pep: 'PEP Status',
  adverse_media: 'Adverse Media & Sanctions',
  business: 'Business Activities',
  wealth: 'Wealth Sources',
  income: 'Income Sources',
  values: 'Client DNA',
};

export const DOMAIN_ORDER: string[] = Object.keys(DOMAIN_LABELS);

/**
 * Split KG entries by verified status.
 * Entries with `verified_by !== null` have been explicitly accepted by a user.
 */
export function partitionEntriesByVerification(entries: KnowledgeGraphEntry[]): {
  verified: KnowledgeGraphEntry[];
  unverified: KnowledgeGraphEntry[];
} {
  const verified: KnowledgeGraphEntry[] = [];
  const unverified: KnowledgeGraphEntry[] = [];
  for (const entry of entries) {
    if (entry.verified_by) {
      verified.push(entry);
    } else {
      unverified.push(entry);
    }
  }
  return { verified, unverified };
}

/**
 * Group entries by domain with display labels, ordered by DOMAIN_ORDER.
 * Domains not in DOMAIN_ORDER are appended at the end.
 */
export function groupEntriesByDomain(
  entries: KnowledgeGraphEntry[]
): Array<{ domain: string; label: string; entries: KnowledgeGraphEntry[] }> {
  const map = new Map<string, KnowledgeGraphEntry[]>();
  for (const entry of entries) {
    const domain = entry.domain || entry.field_path.split('.')[0];
    if (!map.has(domain)) map.set(domain, []);
    map.get(domain)!.push(entry);
  }

  const result: Array<{ domain: string; label: string; entries: KnowledgeGraphEntry[] }> = [];

  // Add known domains in order
  for (const domain of DOMAIN_ORDER) {
    const group = map.get(domain);
    if (group && group.length > 0) {
      result.push({ domain, label: DOMAIN_LABELS[domain], entries: group });
      map.delete(domain);
    }
  }

  // Append any remaining unknown domains
  for (const [domain, group] of map) {
    if (group.length > 0) {
      result.push({
        domain,
        label: domain.charAt(0).toUpperCase() + domain.slice(1).replace(/_/g, ' '),
        entries: group,
      });
    }
  }

  return result;
}
